export type Relation = "left-low" | "equal" | "left-high";

export interface MysteryNumberState {
  known: number;
  target: number;
  coefficient: number;
  unknown: number;
  min: number;
  max: number;
  locked: boolean;
}

export type MysteryNumberAction =
  | { type: "set"; value: number }
  | { type: "increment" }
  | { type: "decrement" }
  | { type: "lock" }
  | { type: "unlock" }
  | { type: "reset"; value: number };

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, Math.round(value)));

export function mysteryTotal(state: MysteryNumberState): number {
  return state.known + state.coefficient * state.unknown;
}

export function mysteryRelation(state: MysteryNumberState): Relation {
  const left = mysteryTotal(state);
  if (left === state.target) return "equal";
  return left < state.target ? "left-low" : "left-high";
}

export function mysteryGap(state: MysteryNumberState): number {
  return state.target - mysteryTotal(state);
}

export function reduceMysteryNumber(
  state: MysteryNumberState,
  action: MysteryNumberAction,
): MysteryNumberState {
  if (state.locked && !["unlock", "reset"].includes(action.type)) return state;
  switch (action.type) {
    case "set":
      return { ...state, unknown: clamp(action.value, state.min, state.max) };
    case "increment":
      return { ...state, unknown: clamp(state.unknown + 1, state.min, state.max) };
    case "decrement":
      return { ...state, unknown: clamp(state.unknown - 1, state.min, state.max) };
    case "lock":
      return { ...state, locked: true };
    case "unlock":
      return { ...state, locked: false };
    case "reset":
      return {
        ...state,
        unknown: clamp(action.value, state.min, state.max),
        locked: false,
      };
  }
}

export function describeMysteryNumber(state: MysteryNumberState): string {
  const left = mysteryTotal(state);
  const relation = mysteryRelation(state);
  if (relation === "equal") {
    return `The left side and right side are both ${state.target}. They have the same value.`;
  }
  const distance = Math.abs(state.target - left);
  return relation === "left-low"
    ? `The left side is ${left}, which is ${distance} below ${state.target}.`
    : `The left side is ${left}, which is ${distance} past ${state.target}.`;
}

export function classifyMissingPartAnswer(
  answer: number,
  known: number,
  target: number,
  correct: number,
  attempts: number,
): string | null {
  if (answer === known + target) return "MN-COMBINE-ALL";
  if (answer === known - target || answer === -correct) {
    return "MN-DIFFERENCE-BACKWARDS";
  }
  if (attempts >= 3) return "MN-IGNORE-RELATION";
  return null;
}
